import classNames from 'classnames';
import React from 'react';
import { Navigate, NavLink, Outlet } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  BACKOFFICE_PERMISSIONS,
  PERMISSIONS,
} from '../../../utils/permissions';
import { RequirePermission } from './RequirePermission';

/** Each section is listed only to users its permission lets in; the routes check it again. */
const SECTIONS = [
  { to: 'tasks', label: 'Tasks', permission: PERMISSIONS.LIST_LABEL_TASK },
  { to: 'imports', label: 'Imports', permission: PERMISSIONS.READ_IMPORT },
  {
    to: 'products',
    label: 'Products',
    permission: PERMISSIONS.READ_PREDICTION,
  },
  {
    to: 'prediction-imports',
    label: 'Prediction imports',
    permission: PERMISSIONS.READ_PREDICTION,
  },
  {
    to: 'migration',
    label: 'Migration',
    permission: PERMISSIONS.ADMIN_MIGRATION,
  },
];

/** `/backoffice` itself: the first section the user may open. */
export function BackofficeIndex() {
  const { has } = usePermissions();
  const first = SECTIONS.find(section => has(section.permission));
  return first ? <Navigate to={first.to} replace /> : null;
}

/**
 * The backoffice: data managers' pages, under `/backoffice`. Rendered inside `GdataLayout`, which makes
 * the user sign in first; this lets in a user with any section's permission (the header links here for
 * them) and lists the sections they may open — each route checks its own permission again.
 */
export function BackofficeLayout() {
  const { has } = usePermissions();
  return (
    <RequirePermission permission={BACKOFFICE_PERMISSIONS}>
      <div className="mx-auto flex max-w-screen-2xl gap-7">
        <nav
          aria-label="Backoffice"
          className="sticky top-24 flex w-52 shrink-0 flex-col gap-0.5 self-start"
        >
          <h2 className="px-3 pb-2 text-sm font-semibold uppercase tracking-wider text-dl-faint">
            Backoffice
          </h2>
          <ul className="flex flex-col gap-0.5">
            {SECTIONS.filter(section => has(section.permission)).map(
              section => (
                <li key={section.to}>
                  <NavLink
                    to={section.to}
                    className={({ isActive }) =>
                      classNames(
                        'block rounded-lg px-3 py-2 text-lg transition',
                        {
                          'bg-dl-tint font-semibold text-dl-accent': isActive,
                          'font-medium text-dl-muted hover:bg-dl-sunken hover:text-dl-text':
                            !isActive,
                        }
                      )
                    }
                  >
                    {section.label}
                  </NavLink>
                </li>
              )
            )}
          </ul>
        </nav>
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </RequirePermission>
  );
}

export default BackofficeLayout;
